import { isEqualAtDepth } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { ObjectWith } from '../../../cross-team-shared/types/misc';

export type DiffEntry = ObjectWith<string>;

interface Selector<Input, Output> {
  getNextLevel: (input: Input) => ObjectWith<Output>;
  keyedBy: string;
}

/**
 * Efficiently search for differences between `previous` and `current` at the
 * locations specified by `selectors`. Selectors pull out the relevant portions
 * of the object in order. There should be one selector per collection in the
 * store that needs to be looped over. Its `getNextLevel` should return an
 * object whose keys can serve as IDs to match objects between `previous` and
 * `current`. The second selector will be passed each consecutive item in the
 * first selector's result, etc.
 *
 * Note that `previous` and `current` are not required to be state objects.
 * However, it will only be efficient when unchanged objects inside them are
 * `===` to each other. Stores are designed to empower this kind of change
 * detection, since they do not mutate unchanged objects.
 *
 * @returns an array of objects, one for each item that changed. Each object
 * simply contains the (key, id) pairs corresponding to each selector. `key`
 * comes from that selector's `keyedBy` property.
 */
export function getDiff<I, A, O>(
  previous: I | undefined,
  current: I | undefined,
  options: {
    selectors?: [Selector<I, A>, Selector<A, O>];
    equalityDepthAtLastLevel?: number;
  },
): DiffEntry[];
export function getDiff<I, A, B, O>(
  previous: I | undefined,
  current: I | undefined,
  options: {
    selectors?: [Selector<I, A>, Selector<A, B>, Selector<B, O>];
    equalityDepthAtLastLevel?: number;
  },
): DiffEntry[];
export function getDiff<I, O>(
  previous: I | undefined,
  current: I | undefined,
  options: {
    selectors?:
      | [Selector<I, O>]
      | [
          Selector<I, unknown>,
          ...Array<Selector<unknown, unknown>>,
          Selector<unknown, O>,
        ];
    equalityDepthAtLastLevel?: number;
  },
): DiffEntry[];
export function getDiff(
  previous: unknown,
  current: unknown,
  {
    selectors = [],
    equalityDepthAtLastLevel = 0,
  }: {
    selectors?: Array<Selector<unknown, unknown>>;
    equalityDepthAtLastLevel?: number;
  },
) {
  const diffRun = new DiffRun(equalityDepthAtLastLevel);
  diffRun.compute(previous, current, selectors, {});
  return diffRun.diff;
}

class DiffRun {
  diff: DiffEntry[] = [];

  constructor(private equalityDepthAtLastLevel: number) {}

  compute(
    previous: unknown,
    current: unknown,
    selectors: Array<Selector<unknown, unknown>>,
    partialEntry: ObjectWith<string>,
  ) {
    if (previous === current) {
      return;
    }
    if (isEmpty(selectors)) {
      if (!isEqualAtDepth(this.equalityDepthAtLastLevel, previous, current)) {
        this.diff.push(partialEntry);
      }
      return;
    }

    const [selector, ...rest] = selectors;
    const prevMap = getNextLevel(previous, selector);
    const curMap = getNextLevel(current, selector);
    const keys = new Set([...Object.keys(prevMap), ...Object.keys(curMap)]);
    for (const key of keys) {
      this.compute(prevMap[key], curMap[key], rest, {
        ...partialEntry,
        [selector.keyedBy]: key,
      });
    }
  }
}

function getNextLevel(
  prevLevel: unknown,
  selector: Selector<unknown, unknown>,
) {
  return prevLevel ? selector.getNextLevel(prevLevel) : {};
}
